import { Table } from 'nhsuk-react-components';
import { Link } from 'react-router-dom';
import { Iteration } from 'schemas/Iteration.schema.ts';
import { unixTimestampToHumanReadableDate, unixTimestampToHumanReadableTime, YYYYMMDDToHumanReadable } from 'utils/formattingUtils.ts';
import './IterationSummaryTable.scss';
import useCampaignNavigate from 'hooks/useCampaignNavigate.ts';
import useCampaign from 'hooks/useCampaign.ts';
import IterationStatus from 'components/IterationStatus';

interface IterationSummaryTableProps {
  iterations: Iteration[];
  userId: string;
}

export default function IterationSummaryTable({
  iterations,
  userId,
}: IterationSummaryTableProps) {
  const { getCampaignPathFor } = useCampaignNavigate();
  const { getDynamoIterationDetails } = useCampaign();

  return (
    <Table
      className="iteration-summary-table"
      data-testid="iteration-summary-table"
    >
      <Table.Head>
        <Table.Row>
          <Table.Cell scope="col" className="nhsuk-u-visually-hidden">
            Lock status
          </Table.Cell>
          <Table.Cell scope="col">Name</Table.Cell>
          <Table.Cell scope="col" className="nhsuk-u-text-align-centre">
            Status
          </Table.Cell>

          <Table.Cell scope="col" className="nhsuk-u-text-align-right">
            Updated by
          </Table.Cell>
        </Table.Row>
      </Table.Head>
      <Table.Body>
        {iterations
          .toSorted((a, b) => {
            if (!a.IterationDate) return 1;
            if (!b.IterationDate) return -1;
            return b.IterationDate.localeCompare(a.IterationDate);
          })
          .map((iteration, idx, arr) => {
            const iterationDetails = getDynamoIterationDetails(iteration.ID || '');
            const isLast = idx === arr.length - 1;
            const className = isLast ? 'last-iteration-row' : undefined;

            return (
              <Table.Row key={iteration.ID}>
                <Table.Cell className={`lock-cell ${className}`}>
                  {(() => {
                    if (iterationDetails?.locked_by?.id) {
                      const isLockedByMe = iterationDetails.locked_by.id === userId;
                      return (
                        <img
                          src="/locked.svg"
                          alt={`Iteration locked by ${iterationDetails.locked_by.name}`}
                          title={`Iteration locked by ${iterationDetails.locked_by.name}`}
                          style={
                isLockedByMe
                  ? { filter: 'invert(25%) sepia(100%) saturate(1500%) hue-rotate(190deg) brightness(75%) contrast(120%)' }
                  : undefined
            }
                        />
                      );
                    }
                    return null;
                  })()}
                </Table.Cell>

                <Table.Cell
                  scope="row"
                  data-testid="iteration-summary-row-name"
                  className={`name-cell ${className}`}
                >
                  <Link
                    to={getCampaignPathFor(`iteration/${iteration.ID}/summary`)}
                    aria-label={`Edit iteration ${iteration.Name || 'Unknown'}`}
                    data-testid={`iteration-${iteration.ID}`}
                  >
                    {iteration.Name || 'Not set'}
                  </Link>

                  <div className="run-date nhsuk-u-margin-top-1">
                    Planned run date:
                    {'    '}
                    {iteration.IterationDate
                      ? YYYYMMDDToHumanReadable(
                        iteration.IterationDate,
                      )
                      : 'Not set'}
                  </div>
                </Table.Cell>
                <Table.Cell className={`nhsuk-u-text-align-centre ${className}`}>
                  <IterationStatus
                    iterationId={iteration.ID as string}
                  />
                </Table.Cell>

                <Table.Cell
                  data-testid="iteration-summary-row-copy-iteration"
                  className={`align-right ${className}`}
                >
                  {
                    (iterationDetails?.last_modified_by && iterationDetails?.last_modified_timestamp) && (
                    <>
                      <p className="nhsuk-u-secondary-text-colour nhsuk-u-margin-bottom-0">{iterationDetails.last_modified_by.name}</p>
                      <p className="nhsuk-body-s nhsuk-u-margin-bottom-0">{unixTimestampToHumanReadableDate(iterationDetails.last_modified_timestamp)}</p>
                      <p className="nhsuk-body-s nhsuk-u-margin-bottom-0">{unixTimestampToHumanReadableTime(iterationDetails.last_modified_timestamp)}</p>
                    </>
                    )
                  }
                </Table.Cell>

              </Table.Row>
            );
          })}
      </Table.Body>
    </Table>
  );
}
